import {
  AccessibleOutlined,
  ApartmentOutlined,
  EmailOutlined,
  HomeOutlined,
  Phone,
  SecurityOutlined,
  WcOutlined,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { Lock } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import HospitalDialog from '../../common/dialog/commonDialogBox';
import { APP_LINK } from '../../common/menu/menuItems/menuList';
import ReusableFormFields from '../../common/textfield/ReusableTextfield';
import { styles } from '../../commonStyle/formStyles';
import { fetchBlocks } from '../../redux/block/blockAction';
import { fetchDepartments } from '../../redux/department/departmentAction';
import { addDoctor, resetCurrentDoctor } from '../../redux/doctor/doctorAction';
import { fetchFloors } from '../../redux/floor/floorAction';
import { fetchAllRoles } from '../../redux/role/roleAction';
import { fetchSpecialists } from '../../redux/specialist/specialistAction';
import { fetchWards } from '../../redux/ward/wardAction';

function AddStaff() {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const currentPage = 1;
  const prevBlockIdRef = useRef(null);
  const prevFloorIdRef = useRef(null);

  const doctorState = useSelector((state) => state?.doctor?.addDoctor || {});
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const specialistList = useSelector(
    (state) => state?.specialist?.fetchSpecialists || {}
  );
  const roleState = useSelector((state) => state?.role?.fetchAllRoles || {});
  useEffect(() => {
    dispatch(fetchAllRoles());
  }, [dispatch]);

  const watchBlock = watch('block_id');
  const watchFloor = watch('floor_id');
  const watchWard = watch('ward_id');
  useEffect(() => {
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
    dispatch(fetchSpecialists(1));
  }, [dispatch]);
  useEffect(() => {
    if (watchBlock && watchBlock !== prevBlockIdRef.current) {
      prevBlockIdRef.current = watchBlock;
      dispatch(fetchFloors(watchBlock, currentPage));
    }
  }, [watchBlock, dispatch]);

  useEffect(() => {
    if (
      watchBlock &&
      watchFloor &&
      (watchBlock !== prevBlockIdRef.current ||
        watchFloor !== prevFloorIdRef.current)
    ) {
      prevBlockIdRef.current = watchBlock;
      prevFloorIdRef.current = watchFloor;
      dispatch(fetchWards(watchBlock, watchFloor, currentPage));
    }
  }, [watchBlock, watchFloor, dispatch]);

  const getBlockOptions = () => {
    return (
      blockList?.Blocks?.data
        ?.filter((block) => block.block_status !== 2)
        .map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || []
    );
  };

  const getFloorOptions = () => {
    return (
      floorList?.floors?.data
        ?.filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || []
    );
  };

  const getWardOptions = () => {
    if (wardList?.wards?.data) {
      return wardList.wards.data
        .filter((ward) => ward.ward_status !== 0)
        .map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        }));
    }
    return [];
  };

  useEffect(() => {
    if (doctorState.error) {
      clearErrors();

      if (doctorState.error === 'email must be unique') {
        setError('email', {
          type: 'manual',
          message: 'Email already exists',
        });
      } else if (doctorState.error === 'phone must be unique') {
        setError('phone', {
          type: 'manual',
          message: 'Phone number already exists',
        });
      } else if (
        doctorState.errorMessage ||
        doctorState.errormessage ||
        doctorState.message
      ) {
        setDialogMessage(
          doctorState.errorMessage ||
            doctorState.errormessage ||
            doctorState.message
        );
        setIsSuccess(false);
        setOpenDialog(true);
      } else {
        setDialogMessage(doctorState.error);
        setIsSuccess(false);
        setOpenDialog(true);
      }
      dispatch(resetCurrentDoctor());
    } else if (doctorState?.success) {
      setDialogMessage({
          title: 'Success',
          message: 'Doctor added successfully.',
          type: 'success',
        });
      setIsSuccess(true);
      setOpenDialog(true);
      setTimeout(() => {
        reset();
        dispatch(resetCurrentDoctor());
      }, 1000);
    }
  }, [doctorState, setError, clearErrors, reset, dispatch]);
  const inputFields = useMemo(
    () => [
      {
        name: 'doctor_id',
        label: 'Doctor Id',
        placeholder: 'Enter Doctor Id',
        type: 'text',
        tabIndex: 1,
        rules: { required: 'Doctor Id is required' },
        startIcon: <SecurityOutlined />,
      },
      {
        name: 'doctor_name',
        label: 'Doctor Name',
        placeholder: 'Enter name',
        type: 'text',
        tabIndex: 2,
        rules: { required: 'Name is required' },
        startIcon: <SecurityOutlined />,
      },
      {
        name: 'email',
        label: 'Email',
        placeholder: 'Enter email',
        type: 'email',
        tabIndex: 3,
        rules: { required: 'Email is required' },
        startIcon: <EmailOutlined />,
      },
      {
        name: 'password',
        label: 'Password',
        placeholder: 'Enter password',
        type: 'password',
        tabIndex: 4,
        maxLength: 10,
        showCharCount: false,
        rules: {
          required: 'Password is required',
          minLength: {
            value: 10,
            message: 'Password must be exactly 10 characters',
          },
          maxLength: {
            value: 10,
            message: 'Password must be exactly 10 characters',
          },
        },
        startIcon: <Lock />,
      },
      {
        name: 'phone',
        label: 'Phone Number',
        placeholder: 'Enter phone number',
        type: 'tel',
        tabIndex: 5,
        maxLength: 10,
        showCharCount: false,
        rules: {
          required: 'Phone number is required',
          pattern: {
            value: /^(6|7|8|9)\d{9}$/,
            message: 'Invalid phone number',
          },
          maxLength: {
            value: 10,
            message: 'Phone number should be 10 digits',
          },
        },
        startIcon: <Phone />,
      },
      {
        name: 'block_id',
        label: 'Block',
        placeholder: 'Select Block',
        type: 'select',
        tabIndex: 6,
        options: getBlockOptions(),
        rules: { required: 'Block is required' },
        startIcon: <ApartmentOutlined />,
      },
      {
        name: 'floor_id',
        label: 'Floor',
        placeholder: 'Select Floor',
        type: 'select',
        tabIndex: 7,
        options: getFloorOptions(),
        rules: { required: 'Floor is required' },
        startIcon: <ApartmentOutlined />,
        // disabled: !watchBlock,
      },
      {
        name: 'ward_id',
        label: 'Ward Name',
        placeholder: 'Select Ward',
        type: 'select',
        tabIndex: 8,
        options: getWardOptions(),
        rules: { required: 'Ward name is required' },
        startIcon: <HomeOutlined />,
        // disabled: !watchBlock || !watchFloor,
      },
      {
        name: 'gender_type',
        label: 'Gender',
        placeholder: 'Select gender',
        type: 'select',
        tabIndex: 9,
        options: [
          { value: 'M', label: 'Male' },
          { value: 'F', label: 'Female' },
          { value: 'Other', label: 'Other' },
        ],
        rules: { required: 'Gender is required' },
        startIcon: <WcOutlined />,
      },
      {
        name: 'address',
        label: 'Address',
        placeholder: 'Enter address',
        type: 'text',
        tabIndex: 10,
        rules: { required: 'Address is required' },
        startIcon: <HomeOutlined />,
      },
      {
        name: 'specialization_id',
        label: 'Specialist',
        placeholder: 'Select Specialist',
        type: 'select',
        tabIndex: 11,
        options:
          specialistList?.specialists?.data?.map((specialist) => ({
            value: specialist.id,
            label: specialist.Name,
          })) || [],
        rules: { required: 'Specialist is required' },
        startIcon: <AccessibleOutlined />,
      },
      {
        name: 'role_id',
        label: 'Role',
        placeholder: 'Select Position',
        type: 'select',
        tabIndex: 12,
        options:
          roleState?.Roles?.data?.map((ward) => ({
            value: ward.id,
            label: ward.name,
          })) || [],
        rules: { required: 'Position is required' },
        startIcon: <WcOutlined />,
      },
    ],
    [blockList, floorList, wardList, specialistList, watchBlock, watchFloor]
  );

  const onSubmit = (data) => {
    const formData = { status: '1', ...data };
    dispatch(addDoctor(formData));
  };

  const handleCancel = () => {
    reset();
    prevBlockIdRef.current = null;
    prevFloorIdRef.current = null;
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleBack = () => {
    navigate(APP_LINK.LISTDOCTOR);
  };
  const navigateToDoctorList = () => {
    navigate(APP_LINK.LISTDOCTOR);
  };
  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper} elevation={2}>
      <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={8} lg={9} xl={11}>
            <Typography variant="h3" sx={styles.heading}>
              Add Doctor
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={4} lg={3} xl={1} mb={2}>
            <Button
              variant="contained"
              color="error"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReusableFormFields
                inputFields={inputFields}
                register={register}
                errors={errors}
                setValue={setValue}
                gridProps={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                control={control}
              />
            </Grid>
          </Grid>

          <Box sx={styles.buttonsContainer}>
            <CustomButton
              type="button"
              text="Clear"
              variant="contained"
              disableElevation
              size="small"
              onClick={handleCancel}
              tabIndex={13}
            />
            <CustomButton
              type="submit"
              text="Submit"
              variant="contained"
              disableElevation
              size="small"
              disabled={doctorState?.inProgress}
              loading={doctorState?.inProgress}
              tabIndex={14}
            />
          </Box>
        </form>
      </Paper>

      <HospitalDialog
        open={openDialog}
        onClose={handleCloseDialog}
        isSuccess={isSuccess}
        dialogMessage={dialogMessage.message}
        title={isSuccess ? 'Doctor Add' : 'Error'}
        navigateTo={navigateToDoctorList}
      />
    </Box>
  );
}

export default AddStaff;
